// The banners at the top of the menu page (ADR 0101): several pictures in a carousel, each
// one optionally opening some dishes when a guest taps it.
//
// A picture is cropped to 16:9 from the middle (the box below shows what will be kept), so
// the owner needs no editor. It uploads straight to storage; the server makes the files and
// the list looks again every few seconds until nothing is processing. Nothing reaches the
// menu page until the next publish.

import { useRef, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../../api/client.js';
import { BANNER_LIMITS, checkBannerFile, uploadBanner } from '../../api/banner-upload.js';
import type { BannerView, MenuDetail } from '../../api/types.js';

const POLL_MS = 3000;
const DEFAULT_LANG = 'en';

export const BANNER_FAILURE_TEXT: Record<string, string> = {
  unreadable: 'We could not read this picture. Try a JPEG or PNG.',
  upload_missing: 'The upload did not arrive. Remove it and add it again.',
};
const failureText = (code: string | null): string =>
  (code && BANNER_FAILURE_TEXT[code]) || 'This picture could not be used. Remove it and try again.';

interface Job {
  readonly id: number;
  readonly name: string;
  readonly progress: number;
  readonly status: 'uploading' | 'done' | 'failed';
  readonly message?: string;
}

const nameOf = (t: readonly { languageCode: string; name: string }[]): string =>
  (t.find((x) => x.languageCode === DEFAULT_LANG) ?? t[0])?.name ?? '(untitled)';

export function BannersPanel(props: {
  readonly orgId: string;
  readonly menuId: string;
  readonly menu: MenuDetail;
  readonly canEdit: boolean;
}): JSX.Element {
  const { orgId, menuId, menu, canEdit } = props;
  const basePath = `/organizations/${orgId}/menus/${menuId}/banners`;
  const listKey = ['banners', orgId, menuId] as const;
  const qc = useQueryClient();

  const [jobs, setJobs] = useState<readonly Job[]>([]);
  const [picked, setPicked] = useState<File[]>([]);
  const [nothing, setNothing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const next = useRef(1);
  const input = useRef<HTMLInputElement | null>(null);

  const list = useQuery({
    queryKey: listKey,
    queryFn: () => apiFetch<{ banners: readonly BannerView[] }>(basePath),
    refetchInterval: (q) =>
      (q.state.data?.banners ?? []).some((b) => b.state === 'processing') ? POLL_MS : false,
    refetchIntervalInBackground: false,
  });
  const banners = list.data?.banners ?? [];
  const full = banners.length >= BANNER_LIMITS.maxPerMenu;
  const refresh = () => qc.invalidateQueries({ queryKey: listKey });

  const update = useMutation({
    mutationFn: (a: { id: string; isShown?: boolean; itemIds?: readonly string[] }) =>
      apiFetch<unknown>(`${basePath}/${a.id}`, {
        method: 'PUT',
        body: {
          ...(a.isShown !== undefined ? { isShown: a.isShown } : {}),
          ...(a.itemIds !== undefined ? { itemIds: a.itemIds } : {}),
        },
      }),
    onSuccess: refresh,
  });
  const reorder = useMutation({
    mutationFn: (bannerIds: readonly string[]) =>
      apiFetch<unknown>(`${basePath}/order`, { method: 'PUT', body: { bannerIds } }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<unknown>(`${basePath}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setRemoving(null);
      return refresh();
    },
  });

  function patch(id: number, change: Partial<Job>): void {
    setJobs((all) => all.map((j) => (j.id === id ? { ...j, ...change } : j)));
  }

  function onPick(files: FileList | null): void {
    const chosen = Array.from(files ?? []);
    if (input.current) input.current.value = '';
    if (chosen.length > 0) {
      setNothing(false);
      setPicked((all) => [...all, ...chosen]);
    }
  }

  async function onCreate(): Promise<void> {
    const chosen = picked;
    if (chosen.length === 0) {
      setNothing(true);
      return;
    }
    setNothing(false);
    setPicked([]);
    setBusy(true);
    let room = BANNER_LIMITS.maxPerMenu - banners.length;
    for (const file of chosen) {
      const id = next.current++;
      const check = checkBannerFile(file);
      const refusal = !check.ok
        ? check.message
        : room <= 0
          ? `A menu can have up to ${BANNER_LIMITS.maxPerMenu} banners.`
          : null;
      if (refusal) {
        setJobs((all) => [
          ...all,
          { id, name: file.name, progress: 0, status: 'failed', message: refusal },
        ]);
        continue;
      }
      setJobs((all) => [...all, { id, name: file.name, progress: 0, status: 'uploading' }]);
      try {
        await uploadBanner({ basePath, file, onProgress: (f) => patch(id, { progress: f }) });
        room -= 1;
        patch(id, { progress: 1, status: 'done' });
        await refresh();
      } catch (err) {
        patch(id, {
          status: 'failed',
          message: err instanceof Error ? err.message : 'upload failed',
        });
      }
    }
    setBusy(false);
  }

  function move(index: number, by: -1 | 1): void {
    const ids = banners.map((b) => b.id);
    const j = index + by;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j]!, ids[index]!];
    reorder.mutate(ids);
  }

  const dishes = menu.categories.flatMap((c) =>
    c.items.map((i) => ({
      id: i.id,
      label: `${nameOf(i.translations)} — ${nameOf(c.translations)}`,
    })),
  );
  const labelOf = (id: string): string =>
    dishes.find((d) => d.id === id)?.label ?? '(unknown dish)';

  return (
    <details className="card banners-panel panel-fold">
      <summary>
        <h2 id="banners-heading">Banners</h2>
        <span className="muted">
          {banners.length === 0
            ? 'none yet'
            : `${banners.length} picture${banners.length === 1 ? '' : 's'}`}
        </span>
      </summary>
      <p className="muted">
        Pictures at the top of the menu page, in a row the guest swipes (it slides by itself until
        they touch it). Each picture is cropped to a wide 16:9 shape from the middle: put what
        matters in the middle. A banner can open some dishes when a guest taps it.
      </p>

      {list.isLoading ? <p className="muted">Loading…</p> : null}
      {list.error ? <p className="error">Could not load the banners.</p> : null}

      {banners.length > 0 ? (
        <ul className="banner-list">
          {banners.map((b, i) => (
            <li key={b.id} className="banner-tile">
              <div className="banner-thumb">
                {b.previewUrl ? (
                  <img src={b.previewUrl} alt="" />
                ) : (
                  <span className="muted">{b.state === 'failed' ? 'Failed' : 'Processing…'}</span>
                )}
              </div>
              <div className="banner-info">
                <strong>
                  Banner {i + 1}
                  {!b.isShown ? ' (hidden)' : ''}
                </strong>
                {b.state === 'failed' ? (
                  <p className="error" role="alert">
                    {failureText(b.failureCode)}
                  </p>
                ) : null}
                <p className="muted">
                  {b.itemIds.length === 0
                    ? 'Opens nothing'
                    : `Opens: ${b.itemIds.map(labelOf).join(', ')}`}
                </p>
                {canEdit ? (
                  <div className="row">
                    <button
                      type="button"
                      aria-label={`move banner ${i + 1} up`}
                      disabled={i === 0 || reorder.isPending}
                      onClick={() => move(i, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={`move banner ${i + 1} down`}
                      disabled={i === banners.length - 1 || reorder.isPending}
                      onClick={() => move(i, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      disabled={update.isPending}
                      onClick={() => update.mutate({ id: b.id, isShown: !b.isShown })}
                    >
                      {b.isShown ? 'Hide' : 'Show'}
                    </button>
                    <button
                      type="button"
                      aria-expanded={linking === b.id}
                      onClick={() => setLinking(linking === b.id ? null : b.id)}
                    >
                      Choose dishes
                    </button>
                    <button type="button" onClick={() => setRemoving(b.id)}>
                      Remove
                    </button>
                  </div>
                ) : null}
                {canEdit && removing === b.id ? (
                  <div className="row" role="alertdialog" aria-label={`Remove banner ${i + 1}`}>
                    <span>Remove this banner? It leaves the menu page after you publish.</span>
                    <button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(b.id)}
                    >
                      Yes, remove
                    </button>
                    <button type="button" onClick={() => setRemoving(null)}>
                      Keep
                    </button>
                  </div>
                ) : null}
                {canEdit && linking === b.id ? (
                  <DishPicker
                    dishes={dishes}
                    chosen={b.itemIds}
                    pending={update.isPending}
                    onSave={(ids) =>
                      update.mutate(
                        { id: b.id, itemIds: ids },
                        { onSuccess: () => setLinking(null) },
                      )
                    }
                  />
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : list.data ? (
        <p className="muted">No banners yet.</p>
      ) : null}

      {(update.error || reorder.error || remove.error) && (
        <p className="error" role="alert">
          {[update.error, reorder.error, remove.error]
            .map((e) => (e instanceof ApiError ? e.message : null))
            .find(Boolean) ?? 'could not save the change'}
        </p>
      )}

      {canEdit ? (
        <>
          <div className="banner-crop-guide" aria-hidden="true">
            <span>16:9: the middle of your picture is kept</span>
          </div>
          <div className="media-add">
            <span className="muted">
              {full
                ? `This menu has all ${BANNER_LIMITS.maxPerMenu} banners it can hold. Remove one to add another.`
                : 'Choose one or more pictures, then press Create banner.'}
            </span>
            <button type="button" disabled={busy || full} onClick={() => input.current?.click()}>
              Choose pictures
            </button>
            <input
              ref={input}
              type="file"
              multiple
              hidden
              accept="image/jpeg,image/png,image/webp,image/avif"
              aria-label="add banner pictures"
              disabled={busy || full}
              onChange={(e) => onPick(e.target.files)}
            />
            {picked.length > 0 ? (
              <ul className="media-jobs" aria-label="pictures ready to add">
                {picked.map((f, i) => (
                  <li key={`${f.name}-${i}`}>
                    {f.name}{' '}
                    <button
                      type="button"
                      onClick={() => setPicked((all) => all.filter((_, j) => j !== i))}
                    >
                      Take out
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <button
              type="button"
              className="primary"
              disabled={busy || full}
              onClick={() => void onCreate()}
            >
              {busy
                ? 'Creating…'
                : picked.length > 1
                  ? `Create ${picked.length} banners`
                  : 'Create banner'}
            </button>
            {nothing ? (
              <p className="error" role="alert">
                Choose a picture first (press Choose pictures), then Create banner.
              </p>
            ) : null}
          </div>
          {jobs.length > 0 ? (
            <ul className="media-jobs">
              {jobs.map((j) => (
                <li key={j.id}>
                  {j.name}:{' '}
                  {j.status === 'uploading' ? (
                    <progress value={j.progress} max={1} />
                  ) : j.status === 'done' ? (
                    'added'
                  ) : (
                    <span className="error" role="alert">
                      {j.message}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </details>
  );
}

function DishPicker(props: {
  readonly dishes: readonly { id: string; label: string }[];
  readonly chosen: readonly string[];
  readonly pending: boolean;
  readonly onSave: (ids: readonly string[]) => void;
}): JSX.Element {
  const [ids, setIds] = useState<readonly string[]>(props.chosen);
  return (
    <div className="banner-dishes">
      <p className="muted">When a guest taps this banner, these dishes open.</p>
      <ul className="discount-dishes">
        {props.dishes.map((d) => (
          <li key={d.id}>
            <label className="radio">
              <input
                type="checkbox"
                checked={ids.includes(d.id)}
                onChange={() =>
                  setIds(ids.includes(d.id) ? ids.filter((x) => x !== d.id) : [...ids, d.id])
                }
              />
              {d.label}
            </label>
          </li>
        ))}
      </ul>
      <button type="button" disabled={props.pending} onClick={() => props.onSave(ids)}>
        Save dishes
      </button>
    </div>
  );
}
